import { useState, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { useApi } from '../../hooks/useApi'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import { apiPut } from '../../lib/auth'
import { Trash2, Shield, User, Save } from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Project {
  id: number
  name: string
  domain: string
}

interface AdminUser {
  id: number
  email: string
  name: string
  role: string
}

interface Grant {
  userId: number
  clientId: number
  role: string
  modules: string[]
  user: { id: number; email: string; name: string }
}

const ROLES = ['owner', 'editor', 'viewer'] as const

const MODULES = [
  'overview',
  'rankings',
  'audit',
  'performance',
  'ai',
  'local',
  'analytics',
  'competitors',
  'backlinks',
  'insights',
  'action-plan',
  'changes',
  'reports',
  'settings',
] as const

const ROLE_STYLES: Record<string, string> = {
  owner: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  editor: 'bg-blue-50 text-blue-700 border-blue-200',
  viewer: 'bg-gray-50 text-gray-600 border-gray-200',
}

// ---------------------------------------------------------------------------
// Helper — DELETE with body (apiDelete in lib/auth has no body support)
// ---------------------------------------------------------------------------

async function deleteGrant(userId: number, clientId: number): Promise<{ error?: string }> {
  const res = await fetch('/api/admin/access/grants', {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, clientId }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.success === false) return { error: json.error ?? 'Erreur' }
  return {}
}

// ---------------------------------------------------------------------------
// Grant form (add / edit)
// ---------------------------------------------------------------------------

function GrantForm({
  clientId,
  users,
  editing,
  onSaved,
  onCancel,
}: {
  clientId: number
  users: AdminUser[]
  editing: Grant | null
  onSaved: () => void
  onCancel: () => void
}) {
  const [userId, setUserId] = useState<number | ''>(editing ? editing.userId : '')
  const [role, setRole] = useState<string>(editing ? editing.role : 'viewer')
  const [modules, setModules] = useState<string[]>(editing ? editing.modules : [])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function toggleModule(m: string) {
    setModules((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (userId === '') {
      setError('Selectionne un utilisateur.')
      return
    }
    setSaving(true)
    setError(null)
    const { error: err } = await apiPut('/api/admin/access/grants', {
      userId,
      clientId,
      role,
      modules,
    })
    setSaving(false)
    if (err) {
      setError(err)
      return
    }
    onSaved()
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-[#E2E4ED] bg-white p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#1B2559]">
          {editing ? "Modifier l'accès" : 'Ajouter un accès'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-[#8B8FA7] hover:underline"
        >
          Annuler
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* User select */}
        <div>
          <label className="mb-1 block text-xs font-medium text-[#8B8FA7]">Utilisateur</label>
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={!!editing}
            className="w-full rounded-lg border border-[#E2E4ED] bg-white px-3 py-2 text-sm text-[#1B2559] focus:border-indigo-400 focus:outline-none disabled:bg-gray-50 disabled:text-[#8B8FA7]"
          >
            <option value="">Selectionner...</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email} ({u.email})
              </option>
            ))}
          </select>
        </div>

        {/* Role select */}
        <div>
          <label className="mb-1 block text-xs font-medium text-[#8B8FA7]">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-lg border border-[#E2E4ED] bg-white px-3 py-2 text-sm text-[#1B2559] focus:border-indigo-400 focus:outline-none"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Modules */}
      <div>
        <label className="mb-2 block text-xs font-medium text-[#8B8FA7]">Modules</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {MODULES.map((m) => (
            <label
              key={m}
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#E2E4ED] px-3 py-2 text-xs text-[#1B2559] hover:bg-[#F8F9FC]"
            >
              <input
                type="checkbox"
                checked={modules.includes(m)}
                onChange={() => toggleModule(m)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
              />
              {m}
            </label>
          ))}
        </div>
      </div>

      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-500 px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-indigo-600 disabled:opacity-50"
        >
          <Save size={13} strokeWidth={1.5} />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Grants list for the selected project
// ---------------------------------------------------------------------------

function GrantsPanel({
  clientId,
  users,
}: {
  clientId: number
  users: AdminUser[]
}) {
  const { data: grants, loading, error, refetch } = useApi<Grant[]>(
    `/api/admin/access/projects/${clientId}/grants`,
  )
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Grant | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  async function handleDelete(userId: number) {
    if (!window.confirm('Retirer cet accès ?')) return
    setDeletingId(userId)
    await deleteGrant(userId, clientId)
    setDeletingId(null)
    refetch()
  }

  function handleSaved() {
    setShowForm(false)
    setEditing(null)
    refetch()
  }

  const list = grants ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#1B2559]">
          Accès du projet ({list.length})
        </h2>
        {!showForm && !editing && (
          <button
            type="button"
            onClick={() => {
              setEditing(null)
              setShowForm(true)
            }}
            className="rounded-lg bg-indigo-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-600"
          >
            + Ajouter un accès
          </button>
        )}
      </div>

      {(showForm || editing) && (
        <GrantForm
          key={editing ? `edit-${editing.userId}` : 'new'}
          clientId={clientId}
          users={users}
          editing={editing}
          onSaved={handleSaved}
          onCancel={() => {
            setShowForm(false)
            setEditing(null)
          }}
        />
      )}

      {loading && (
        <div className="flex h-32 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-center text-sm text-red-600">
          Erreur lors du chargement des accès : {error}
        </div>
      )}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-xl border border-[#E2E4ED] bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#E2E4ED] bg-[#F8F9FC]">
                <th className="px-5 py-3 font-medium text-[#8B8FA7]">Utilisateur</th>
                <th className="px-5 py-3 font-medium text-[#8B8FA7]">Role</th>
                <th className="px-5 py-3 font-medium text-[#8B8FA7]">Modules</th>
                <th className="px-5 py-3 text-right font-medium text-[#8B8FA7]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((g) => (
                <tr
                  key={g.userId}
                  className="border-b border-[#E2E4ED] last:border-b-0 hover:bg-[#F8F9FC]/50"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-600">
                        {(g.user?.name || g.user?.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-[#1B2559]">{g.user?.name || '-'}</p>
                        <p className="truncate text-xs text-[#8B8FA7]">{g.user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-xs font-medium ${ROLE_STYLES[g.role] ?? ROLE_STYLES.viewer}`}
                    >
                      {g.role === 'owner' ? (
                        <Shield size={11} strokeWidth={1.5} />
                      ) : (
                        <User size={11} strokeWidth={1.5} />
                      )}
                      {g.role}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {g.modules.length === 0 && <span className="text-xs text-[#8B8FA7]">-</span>}
                      {g.modules.map((m) => (
                        <span
                          key={m}
                          className="rounded border border-[#E2E4ED] bg-[#F8F9FC] px-1.5 py-0.5 text-[11px] text-[#1B2559]"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false)
                          setEditing(g)
                        }}
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(g.userId)}
                        disabled={deletingId === g.userId}
                        className="text-red-400 hover:text-red-600 disabled:opacity-50"
                        aria-label="Supprimer"
                      >
                        <Trash2 size={15} strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {list.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-[#8B8FA7]">
                    Aucun accès pour ce projet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function ProjectAccess() {
  const { isAdmin, loading: userLoading } = useCurrentUser()
  const { data: projects, loading: projectsLoading, error: projectsError } =
    useApi<Project[]>('/api/clients')
  const { data: users, loading: usersLoading } = useApi<AdminUser[]>('/api/admin/users')
  const [selectedId, setSelectedId] = useState<number | ''>('')

  const projectList = useMemo(() => (Array.isArray(projects) ? projects : []), [projects])
  const userList = useMemo(() => (Array.isArray(users) ? users : []), [users])

  // Admin guard — placé APRÈS tous les hooks (Rules of Hooks)
  if (!userLoading && !isAdmin) {
    return <Navigate to="/projects" replace />
  }

  if (userLoading || projectsLoading || usersLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      </div>
    )
  }

  if (projectsError) {
    return (
      <div className="rounded-xl border border-red-300 bg-red-50 p-6 text-center text-red-600">
        Erreur lors du chargement des projets : {projectsError}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1B2559]">Accès projets</h1>
        <p className="mt-1 text-sm text-[#8B8FA7]">
          Gérer les accès des utilisateurs à chaque projet
        </p>
      </div>

      {/* Project selector */}
      <div className="rounded-xl border border-[#E2E4ED] bg-white p-5">
        <label className="mb-1 block text-xs font-medium text-[#8B8FA7]">Projet</label>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value === '' ? '' : Number(e.target.value))}
          className="w-full max-w-md rounded-lg border border-[#E2E4ED] bg-white px-3 py-2 text-sm text-[#1B2559] focus:border-indigo-400 focus:outline-none"
        >
          <option value="">Selectionner un projet...</option>
          {projectList.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.domain})
            </option>
          ))}
        </select>
      </div>

      {/* Grants */}
      {selectedId !== '' && (
        <GrantsPanel key={selectedId} clientId={selectedId} users={userList} />
      )}
    </div>
  )
}
